import type {
  CandidateGroup,
  CandidateQueue,
  ExpectedRevision,
  PersonCard,
  RegistrationSummary,
} from '@tabel/shared/personnel';
import type { MeResponse } from '@tabel/shared/schemas/auth';
import { contextHeaders } from './role';

export type Expected = ExpectedRevision[];
export type UtcRange = { fromMs: number | null; toMs: number | null };
export type DateRange = { fromDate: string; toDateExclusive: string | null };
export type Envelope<T> = {
  data: T;
  revisions: Expected;
  processing: 'ready' | 'pending';
  request_id?: string;
};
export type Page<T> = {
  items: T[];
  nextCursor: string | null;
  snapshotRevision: string;
  freshness?: Freshness;
  allowedActions?: string[];
};
export type Freshness = {
  state: 'fresh' | 'pending' | 'failed';
  asOfMs: number;
  computedAtMs: number | null;
  validThroughMs?: number | null;
};
export type Person = PersonCard;
export type Observation = {
  field?: string;
  rawValue?: string | null;
  sourceKey?: string;
  sourceVersion?: string;
  orderingBasis?: string;
  id?: string;
  displayName?: string | null;
  rawName?: string;
  department?: string | null;
  section?: string | null;
  position?: string | null;
  localStatus?: string;
  status?: string;
  receivedAtMs?: number;
  exportedAtMs?: number | null;
  sourceFileId?: string;
  rowNumber?: number;
  sourceRevision?: string;
};
export type Registration = RegistrationSummary & {
  siteName?: string;
  observations?: Observation[];
  aliases?: Alias[];
  firstPassAtMs?: number | null;
  passCount?: number;
};
export type Alias = {
  id: string;
  personId: string;
  fromMs: number | null;
  toMs: number | null;
  state?: string;
};
export type Membership = {
  id: string;
  personId: string;
  displayName?: string;
  brigadeId: string;
  brigadeName?: string;
  fromDate: string;
  toDateExclusive: string | null;
  revision?: number;
};
export type Group = CandidateGroup;
export type GroupPage = CandidateQueue & {
  readiness?: {
    state: 'ready' | 'pending' | 'failed';
    reasonCode?: string;
    jobId?: string;
    refreshAfterMs?: number;
  };
};
export function queueState(queue?: GroupPage): 'ready' | 'pending' | 'failed' {
  return (
    queue?.readiness?.state ??
    (queue?.freshness?.state === 'fresh' ? 'ready' : (queue?.freshness?.state ?? 'pending'))
  );
}
export type Preview = {
  rows?: number;
  previewToken: string;
  expectedRevisions?: Expected;
  revisions?: Expected;
  expiresAtMs?: number;
  consequences?: string[];
  conflicts?: { code: string; message?: string }[];
  affectedPeople?: string[];
  operation_date?: string;
  effects?: { personIds: string[]; registrationIds: string[]; action: string };
  allowed?: boolean;
};
export type Brigade = {
  id: string;
  name: string;
  state?: string;
  revision: number;
  allowedActions?: string[];
  primaryLeader?: { assignmentId?: string; userId: number; displayName?: string | null } | null;
  history?: {
    id: string;
    entityKind: string;
    entityId: string;
    revision: number;
    atMs: number;
    actorUserId: number;
    data: unknown;
  }[];
  leaderName?: string | null;
  memberCount?: number;
  memberships?: Membership[];
  activity?: { fromDate: string; toDateExclusive: string | null }[];
};
export type Decision = {
  origin?: 'automatic' | 'manual';
  policyVersion?: string;
  input?: { basis?: string; registrationIds?: string[]; personIds?: string[] };
  undoOf?: string | null;
  id: string;
  action: string;
  basis?: string;
  createdAtMs?: number;
  personIds?: string[];
  registrationIds?: string[];
  revision?: number;
  allowedActions?: string[];
};
export type Binding = { id: string; userId: number; personId: string; state?: string; revision?: number };
export type Assignment = {
  id: string;
  role: string;
  userId: number;
  scope: { kind: string; brigadeId?: string; siteId?: string };
  fromMs: number;
  toMs: number | null;
  allowedActions?: string[];
  revision: number;
};
export type DirectoryCase = {
  id: string;
  reasonCode: string;
  state: string;
  revision: number;
  message?: string;
  assigneeUserId?: number | null;
  allowedActions?: string[];
  allowedActionRefs?: { kind: string; targetId: string; href: string }[];
};
export type DirectoryCapabilitiesMe = MeResponse & { directoryCapabilities?: string[] };
export const capabilities = (me?: MeResponse): string[] =>
  (me as DirectoryCapabilitiesMe | undefined)?.directoryCapabilities ?? [];

export class DirectoryError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
  ) {
    super(message);
  }
}

/** Personnel requests never enter the agent API or the client-error logger with source contents. */
export async function directoryRequest<T>(
  path: string,
  method = 'GET',
  body?: unknown,
): Promise<Envelope<T>> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'include',
      headers: { ...contextHeaders(), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new DirectoryError(0, 'network', 'Нет связи с сервером. Введённые данные сохранены.', null);
  }
  const result: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const source = (result ?? {}) as {
      error?: { code?: string; message?: string; requestId?: string; request_id?: string };
      code?: string;
      message?: string;
      request_id?: string;
    };
    const error = source.error ?? source;
    throw new DirectoryError(
      response.status,
      error.code ?? 'internal',
      error.message ?? 'Не удалось выполнить запрос',
      ('request_id' in error ? error.request_id : undefined) ??
        ('requestId' in error ? error.requestId : undefined) ??
        response.headers.get('x-request-id'),
    );
  }
  if (!result || typeof result !== 'object')
    throw new DirectoryError(
      500,
      'invalid_response',
      'Сервер вернул неполный ответ. Обновите данные.',
      response.headers.get('x-request-id'),
    );
  if (result && typeof result === 'object' && 'data' in result) return result as Envelope<T>;
  // Read endpoints may return their named DTO directly; mutations always use the command envelope.
  return { data: result as T, revisions: [], processing: 'ready' };
}
export function command<T>(
  input: T,
  expectedRevisions: Expected,
  previewToken?: string,
  requestId: string = crypto.randomUUID(),
) {
  return { request_id: requestId, expectedRevisions, ...(previewToken ? { previewToken } : {}), input };
}
export function queryString(values: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(values)) if (value) params.set(name, value);
  return params.size ? `?${params}` : '';
}
export const dateTime = (ms?: number | null) =>
  ms == null
    ? 'Не указано'
    : new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'short' }).format(ms);
export const today = () => new Date().toISOString().slice(0, 10);
